(() => {
  if (customElements.get('moebe-mobile-menu')) return;
  class MoebeMobileMenu extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('#NavDrawer');
      this.body = this.querySelector('.moebe-menu__body');
      this.rootPanel = this.querySelector('[data-menu-root]');
      if (!this.drawer || !this.rootPanel) return;
      this.mobile = matchMedia('(max-width: 768px)');
      this.prepareMenuIcon();
      this.stack = [];
      this.current = this.rootPanel;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.addEventListener('click', event => this.headerClick(event), {...options, capture: true});
      this.addEventListener('click', event => this.click(event), options);
      this.drawer.addEventListener('keydown', event => this.keydown(event), {...options, capture: true});
      document.addEventListener('drawerOpen.NavDrawer', () => this.sync(true), options);
      this.onBreakpoint = () => {
        if (this.drawer.classList.contains('drawer--is-open')) window.theme?.NavDrawer?.close();
        this.sync();
      };
      this.mobile.addEventListener('change', this.onBreakpoint);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.drawer, {attributes: true, attributeFilter: ['class']});
      this.classList.add('is-ready');
      this.sync();
    }
    disconnectedCallback() {
      this.restoreHeader();
      this.animateMenuIcon(false, [], false);
      this.controller?.abort();
      this.observer?.disconnect();
      this.mobile?.removeEventListener('change', this.onBreakpoint);
      document.documentElement.classList.remove('moebe-mobile-menu-open');
    }
    sync(focus = false) {
      const open = this.mobile.matches && this.drawer.classList.contains('drawer--is-open') && window.theme?.NavDrawer?.isOpen !== false;
      const changed = open !== Boolean(this.open);
      const iconFrames = changed ? this.iconPaths.map(path => {
        const style = getComputedStyle(path);
        return {transform: style.transform || 'none', opacity: style.opacity || '1'};
      }) : [];
      if (this.mobile.matches && this.drawer.classList.contains('is-transitioning')) this.drawer.classList.remove('is-transitioning');
      document.documentElement.classList.toggle('moebe-mobile-menu-open', open);
      if (open) {
        if (!this.open) { this.dockHeader(); this.body.scrollTop = 0; }
        this.drawer.setAttribute('role', 'dialog');
        this.drawer.setAttribute('aria-modal', 'true');
        this.drawer.setAttribute('aria-label', this.getAttribute('aria-label'));
        if (!this.open || focus) this.focus(this.closeControl());
      } else {
        this.restoreHeader();
        this.drawer.removeAttribute('role');
        this.drawer.removeAttribute('aria-modal');
        this.drawer.removeAttribute('aria-label');
        this.reset();
      }
      if (changed) this.animateMenuIcon(open, iconFrames, this.mobile.matches);
      this.open = open;
    }
    prepareMenuIcon() {
      this.iconPaths = [];
      this.iconAnimations = [];
      document.querySelectorAll('#SiteHeader .js-drawer-open-nav .icon-hamburger').forEach(icon => {
        if (!icon.classList.contains('moebe-menu-icon')) {
          const original = icon.querySelector('path');
          if (!original) return;
          const lines = ['M7 15h51', 'M7 32h43', 'M7 49h51'].map(d => {
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', d);
            return path;
          });
          original.replaceWith(...lines);
          icon.classList.add('moebe-menu-icon');
        }
        this.iconPaths.push(...icon.querySelectorAll('path'));
      });
    }
    animateMenuIcon(open, frames, animate = true) {
      this.iconAnimations.forEach(animation => animation.cancel());
      this.iconAnimations = [];
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.iconPaths.forEach((path, index) => {
        const line = index % 3;
        const target = {
          transform: open ? ['translateY(17px) rotate(45deg)', 'scaleX(0)', 'translateY(-17px) rotate(-45deg)'][line] : 'none',
          opacity: open && line === 1 ? '0' : '1'
        };
        Object.assign(path.style, target);
        // Animate after reparenting: moving the header can cancel CSS transitions.
        if (animate && !reducedMotion && path.animate && frames[index]) {
          this.iconAnimations.push(path.animate([frames[index], target], {duration: 180, easing: 'ease-in-out'}));
        }
      });
    }
    closeControl() {
      return Array.from(this.siteHeader?.querySelectorAll('.js-drawer-open-nav') || []).find(button => button.offsetParent !== null) || this.querySelector('[data-menu-close]');
    }
    dockHeader() {
      const header = document.getElementById('SiteHeader');
      const slot = this.querySelector('[data-menu-header]');
      if (!header || !slot || this.headerPlaceholder) return;
      const bounds = header.getBoundingClientRect();
      this.drawer.style.setProperty('--moebe-menu-top', `${Math.max(0, Math.min(bounds.top, window.innerHeight / 3))}px`);
      this.siteHeader = header;
      this.headerPlaceholder = document.createElement('div');
      this.headerPlaceholder.style.height = `${bounds.height}px`;
      this.headerPlaceholder.setAttribute('aria-hidden', 'true');
      header.before(this.headerPlaceholder);
      this.headerLabels = Array.from(header.querySelectorAll('.js-drawer-open-nav')).map(button => ({button, label: button.getAttribute('aria-label')}));
      this.headerLabels.forEach(({button}) => button.setAttribute('aria-label', this.dataset.closeLabel));
      slot.append(header);
      slot.classList.add('has-site-header');
    }
    restoreHeader() {
      if (!this.headerPlaceholder) return;
      this.headerPlaceholder.replaceWith(this.siteHeader);
      this.headerPlaceholder = null;
      this.headerLabels.forEach(({button, label}) => {
        if (label === null) button.removeAttribute('aria-label');
        else button.setAttribute('aria-label', label);
        button.setAttribute('aria-expanded', 'false');
      });
      this.querySelector('[data-menu-header]')?.classList.remove('has-site-header');
      this.drawer.style.removeProperty('--moebe-menu-top');
    }
    headerClick(event) {
      if (!this.open || !this.siteHeader?.contains(event.target)) return;
      const control = event.target.closest('a, button');
      if (!control) return;
      if (control.matches('.js-drawer-open-nav')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.theme?.NavDrawer?.close();
        this.sync();
        return;
      }
      const drawer = window.theme?.NavDrawer;
      if (drawer) {
        drawer.activeSource = null;
        drawer.close();
      }
      this.sync();
    }
    reset() {
      this.querySelectorAll('details[open]').forEach(details => details.removeAttribute('open'));
      this.querySelectorAll('[data-menu-panel]').forEach(panel => {
        panel.hidden = panel !== this.rootPanel;
        panel.classList.remove('is-entering', 'is-returning');
      });
      this.querySelectorAll('[data-menu-forward]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.stack = [];
      this.current = this.rootPanel;
      this.body.scrollTop = 0;
    }
    click(event) {
      const close = event.target.closest('[data-menu-close]');
      if (close) { window.theme?.NavDrawer?.close(); this.sync(); return; }
      const forward = event.target.closest('[data-menu-forward]');
      if (forward) {
        const next = document.getElementById(forward.dataset.menuForward);
        if (!next || !this.contains(next)) return;
        this.stack.push({panel: this.current, trigger: forward, scroll: this.body.scrollTop});
        forward.setAttribute('aria-expanded', 'true');
        this.show(next, 'is-entering');
        this.body.scrollTop = 0;
        this.focus(next.querySelector('[data-menu-back]'));
      }
      if (event.target.closest('[data-menu-back]') && this.stack.length) {
        const previous = this.stack.pop();
        this.show(previous.panel, 'is-returning');
        previous.trigger.setAttribute('aria-expanded', 'false');
        this.body.scrollTop = previous.scroll;
        this.focus(previous.trigger);
      }
    }
    show(panel, animation) {
      this.current.hidden = true;
      this.current.classList.remove('is-entering', 'is-returning');
      panel.hidden = false;
      panel.classList.add(animation);
      this.current = panel;
    }
    focus(element) {
      // Refresh Impulse's focus boundaries after replacing the visible panel.
      window.theme?.a11y?.trapFocus({container: this.drawer, namespace: 'drawer_focus', elementToFocus: element});
      element?.focus({preventScroll: true});
    }
    keydown(event) {
      if (!this.open || event.key !== 'Tab') return;
      const elements = Array.from(this.querySelectorAll('a[href], button, input, select, summary, [tabindex="0"]'))
        .filter(element => !element.disabled && element.offsetParent !== null && !element.closest('[hidden]'));
      const first = elements[0], last = elements[elements.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.drawer)) {
        event.preventDefault(); event.stopImmediatePropagation(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); event.stopImmediatePropagation(); first.focus();
      }
    }
  }
  customElements.define('moebe-mobile-menu', MoebeMobileMenu);
})();

